import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import PowerIcon from "@mui/icons-material/Power";
import WifiIcon from "@mui/icons-material/Wifi";
import PeopleIcon from "@mui/icons-material/People";
import BoltIcon from "@mui/icons-material/Bolt";
import { useAuthApi } from "../hooks/useAuthApi";
import { BIZ, MGMT, OCPP } from "../env";
import type { Charger, Contract, Transaction } from "../types";

type HealthState = "loading" | "ok" | "error";
interface Health {
  state: HealthState;
  message: string;
}

interface ActiveSessionSeriesPoint {
  timestamp: string;
  active_sessions: number;
}

interface ContractFindResponse {
  items: Contract[];
  total: number;
  count: number;
  limit: number;
  offset: number;
}

function HealthCard({ title, health }: { title: string; health: Health }) {
  const sev =
    health.state === "ok" ? "success" : health.state === "error" ? "error" : "info";
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Alert severity={sev} variant="outlined">
          {health.message}
        </Alert>
      </CardContent>
    </Card>
  );
}

function StatCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: `${color}1a`,
              color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function ActiveSessionsTimeSeriesChart({ points }: { points: ActiveSessionSeriesPoint[] }) {
  const width = 860;
  const height = 260;
  const padding = { top: 18, right: 24, bottom: 40, left: 52 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const maxY = Math.max(1, ...points.map((point) => point.active_sessions));
  const ticks = [0, Math.ceil(maxY / 2), maxY];
  const lastIndex = Math.max(points.length - 1, 1);

  const polyline = points
    .map((point, index) => {
      const x = padding.left + (index / lastIndex) * plotWidth;
      const y = padding.top + ((maxY - point.active_sessions) / maxY) * plotHeight;
      return `${x},${y}`;
    })
    .join(" ");

  const xLabel = (index: number) =>
    new Date(points[index].timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        Y-axis: Sessions
      </Typography>
      <Box component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", height: "auto" }}>
        {ticks.map((tick) => {
          const y = padding.top + ((maxY - tick) / maxY) * plotHeight;
          return (
            <g key={tick}>
              <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke="currentColor" opacity={0.2} />
              <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="currentColor">
                {tick}
              </text>
            </g>
          );
        })}
        <polyline fill="none" stroke="currentColor" strokeWidth="2.5" points={polyline} />
        {[0, Math.floor(lastIndex / 2), lastIndex].map((index) => {
          const x = padding.left + (index / lastIndex) * plotWidth;
          return (
            <text key={`${index}-${x}`} x={x} y={height - 12} textAnchor="middle" fontSize="11" fill="currentColor">
              {xLabel(index)}
            </text>
          );
        })}
      </Box>
    </Box>
  );
}

export default function Dashboard() {
  const api = useAuthApi();
  const [ocpp, setOcpp] = useState<Health>({ state: "loading", message: "Checking…" });
  const [biz, setBiz] = useState<Health>({ state: "loading", message: "Checking…" });
  const [activeSeries, setActiveSeries] = useState<ActiveSessionSeriesPoint[]>([]);
  const [activeSeriesError, setActiveSeriesError] = useState<string | null>(null);
  const [activeSeriesLoading, setActiveSeriesLoading] = useState(true);
  const [stats, setStats] = useState({
    chargers: 0,
    connected: 0,
    drivers: 0,
    transactions: 0,
  });

  const loadActiveContractDriverCount = async () => {
    const uniqueDriverIds = new Set<number>();
    const limit = 200;
    let offset = 0;

    while (true) {
      const page = await api<ContractFindResponse>(`${MGMT}/contracts/find`, {
        method: "POST",
        body: JSON.stringify({
          is_active: true,
          limit,
          offset,
        }),
      });
      const items = page?.items ?? [];
      items.forEach((contract) => uniqueDriverIds.add(contract.driver_id));
      offset += items.length;
      if (items.length === 0 || offset >= (page?.total ?? 0)) break;
    }

    return uniqueDriverIds.size;
  };

  useEffect(() => {
    (async () => {
      try {
        await api(`${OCPP}/health`);
        setOcpp({ state: "ok", message: "Healthy" });
      } catch (e) {
        setOcpp({ state: "error", message: (e as Error).message });
      }
      try {
        await api(`${BIZ}/health`);
        setBiz({ state: "ok", message: "Healthy" });
      } catch (e) {
        setBiz({ state: "error", message: (e as Error).message });
      }
      try {
        const [chargers, transactions, activeContractDrivers] = await Promise.all([
          api<Charger[]>(`${OCPP}/chargers`),
          api<Transaction[]>(`${OCPP}/transactions`),
          loadActiveContractDriverCount(),
        ]);
        setStats({
          chargers: chargers?.length ?? 0,
          connected: (chargers ?? []).filter((c) => c.is_connected).length,
          drivers: activeContractDrivers,
          transactions: transactions?.length ?? 0,
        });
      } catch {
        /* ignore — already surfaced via health */
      }
      setActiveSeriesLoading(true);
      setActiveSeriesError(null);
      try {
        const series = await api<ActiveSessionSeriesPoint[]>(
          `${MGMT}/sessions/active-timeseries?hours=24&bucket_minutes=60`
        );
        setActiveSeries(series ?? []);
      } catch (e) {
        setActiveSeriesError((e as Error).message);
      } finally {
        setActiveSeriesLoading(false);
      }
    })();
  }, []);

  const activeNow = activeSeries[activeSeries.length - 1]?.active_sessions ?? 0;

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <HealthCard title="OCPP Service" health={ocpp} />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <HealthCard title="Business Service" health={biz} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard label="Chargers" value={stats.chargers} icon={<PowerIcon />} color="#6366f1" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard label="Connected" value={stats.connected} icon={<WifiIcon />} color="#16a34a" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard label="Drivers" value={stats.drivers} icon={<PeopleIcon />} color="#2563eb" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard label="Transactions" value={stats.transactions} icon={<BoltIcon />} color="#ca8a04" />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Active transactions (last 24h)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Current active sessions: {activeNow}
            </Typography>
            {activeSeriesLoading ? (
              <Box sx={{ minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : activeSeriesError ? (
              <Typography color="error">{activeSeriesError}</Typography>
            ) : activeSeries.length < 2 ? (
              <Typography color="text.secondary">No session activity yet.</Typography>
            ) : (
              <ActiveSessionsTimeSeriesChart points={activeSeries} />
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
